import {
    Add,
    Delete,
    DragIndicator,
    Edit,
    ExpandLess,
    ExpandMore,
    FilterListOff,
    Folder,
    FolderOpen,
    Visibility,
    VisibilityOff,
} from "@mui/icons-material";
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import config from "../../../config";
import { uploadFileOnFileSystem } from "../../../shared/hooks/useCreateResources";
import { useUpdateDocumentTitle } from "../../../shared/hooks/useDoctype";
import {
    useCreateDoctype,
    useUpdateDoctypeData,
} from "../../../shared/hooks/useDocTypesData";
import {
    useDeleteDoctype,
    useFrappeToken,
    useLoadDoctypeMitation,
    useSaveDoc,
} from "../../../shared/hooks/useFrappeDoctype";

const ItemGroupsTab = ({
    itemGroups,
    onEdit,
    refreshTrigger,
    refetchItemGroups,
}) => {
    const queryClient = useQueryClient();
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [parentGroups, setParentGroups] = useState([]);
    const [childGroups, setChildGroups] = useState([]);
    const [filteredChildGroups, setFilteredChildGroups] = useState([]);
    const [selectedParentFilters, setSelectedParentFilters] = useState(
        new Set()
    );
    const [showAllItemGroupsChildren, setShowAllItemGroupsChildren] =
        useState(false);
    const [editingName, setEditingName] = useState(null);
    const [newName, setNewName] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [updatingName, setUpdatingName] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemGroupToDelete, setItemGroupToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const deleteItemGroup = useDeleteDoctype();
    const updateItemGroup = useUpdateDoctypeData();
    const createItemGroup = useCreateDoctype();
    const updateDocumentTitle = useUpdateDocumentTitle();
    const saveDoc = useSaveDoc();
    const { data: frappeToken } = useFrappeToken(true);
    const loadDoctype = useLoadDoctypeMitation();

    useEffect(() => {
        organizeGroups();
        // Reset drag state when data changes
        setIsDragging(false);
    }, [itemGroups, refreshTrigger]);

    // Update filtered child groups when child groups or selected filters change
    useEffect(() => {
        let filtered = childGroups;

        if (showAllItemGroupsChildren) {
            // Filter to show only child groups with "All Item Groups" as parent
            filtered = filtered.filter(
                (child) => child.parent_item_group === "All Item Groups"
            );
        } else if (selectedParentFilters.size > 0) {
            // Filter by selected parent groups
            filtered = filtered.filter((child) =>
                selectedParentFilters.has(child.parent_item_group)
            );
        }

        setFilteredChildGroups(filtered);
    }, [childGroups, selectedParentFilters, showAllItemGroupsChildren]);

    const organizeGroups = () => {
        if (!itemGroups || itemGroups.length === 0) return;

        // Separate groups by is_group field and filter by show_pos = 1
        const parents = itemGroups.filter(
            (group) =>
                group.is_group === 1 &&
                group.item_group_name !== "All Item Groups" &&
                group.showpos === 1
        );
        const children = itemGroups.filter(
            (group) =>
                (group.is_group === 0 ||
                    group.is_group === null ||
                    group.is_group === undefined) &&
                group.showpos === 1
        );

        // Organize parent groups in hierarchy (excluding "All Item Groups")
        const parentMap = {};
        const rootParents = [];

        parents.forEach((group) => {
            parentMap[group.item_group_name] = {
                ...group,
                children: [],
            };
        });

        parents.forEach((group) => {
            if (
                group.parent_item_group &&
                group.parent_item_group !== "All Item Groups" &&
                parentMap[group.parent_item_group]
            ) {
                parentMap[group.parent_item_group].children.push(
                    parentMap[group.item_group_name]
                );
            } else {
                rootParents.push(parentMap[group.item_group_name]);
            }
        });

        setParentGroups(rootParents);
        setChildGroups(children);
        setFilteredChildGroups(children); // Show all initially
    };

    const handleToggleExpand = (groupName) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupName)) {
            newExpanded.delete(groupName);
        } else {
            newExpanded.add(groupName);
        }
        setExpandedGroups(newExpanded);
    };

    const handleFilterToggle = (parentGroupName) => {
        const newFilters = new Set(selectedParentFilters);
        if (newFilters.has(parentGroupName)) {
            // Remove filter
            newFilters.delete(parentGroupName);
        } else {
            // Add filter
            newFilters.add(parentGroupName);
        }
        setSelectedParentFilters(newFilters);
    };

    const handleResetAllFilters = () => {
        setSelectedParentFilters(new Set());
        setShowAllItemGroupsChildren(false);
    };

    const handleToggleAllItemGroupsFilter = () => {
        setShowAllItemGroupsChildren(!showAllItemGroupsChildren);
        // Clear other filters when toggling this one
        if (!showAllItemGroupsChildren) {
            setSelectedParentFilters(new Set());
        }
    };

    const handleAddParentGroup = () => {
        // Create new parent group with is_group = 1
        onEdit(null, { is_group: 1 });
    };

    const handleAddChildGroup = () => {
        // Create new child group with is_group = 0
        onEdit(null, { is_group: 0 });
    };

    const handleAvatarClick = (group) => {
        // Create a file input to select image
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
                setUploadingImage(group.item_group_name);
                try {
                    // Step 1: Upload file using uploadFileOnFileSystem
                    const uploadResponse = await uploadFileOnFileSystem(
                        file,
                        null, // onUploadProgress
                        group.item_group_name, // docname
                        "Item Group", // doctype
                        "image", // fieldname
                        frappeToken // csrfToken
                    );

                    const uploadResult = await uploadResponse.json();

                    if (uploadResult.message && uploadResult.message.file_url) {
                        // Step 2: Fetch the latest document from database
                        const latestDocResponse = await loadDoctype.mutateAsync(
                            {
                                doctype: "Item Group",
                                name: group.item_group_name,
                            }
                        );

                        const latestDoc = latestDocResponse.data.docs[0];

                        // Step 3: Update the latest document with new image URL
                        const updatedGroup = {
                            ...latestDoc,
                            image: uploadResult.message.file_url,
                            __unsaved: 1,
                        };

                        await saveDoc.mutateAsync({
                            payload: updatedGroup,
                            action: "Save",
                        });

                        enqueueSnackbar("Image uploaded successfully", {
                            variant: "success",
                        });

                        // Invalidate item group queries
                        queryClient.invalidateQueries(["getItemGroups"]);
                        queryClient.invalidateQueries(["getGroupSubGroups"]);

                        // Refetch data to show updated image
                        refetchItemGroups();
                    } else {
                        throw new Error("Invalid upload response");
                    }
                } catch (error) {
                    console.error("Upload error:", error);
                    enqueueSnackbar("Failed to upload image", {
                        variant: "error",
                    });
                } finally {
                    setUploadingImage(null);
                }
            }
        };
        input.click();
    };

    const handleNameClick = (group) => {
        setEditingName(group.item_group_name);
        setNewName(group.item_group_name);
    };

    const handleNameSave = async (oldName) => {
        if (newName && newName !== oldName) {
            setUpdatingName(oldName);
            try {
                await updateDocumentTitle.mutateAsync({
                    doctype: "Item Group",
                    docname: oldName,
                    name: newName,
                });
                enqueueSnackbar("Item Group name updated successfully", {
                    variant: "success",
                });
                // Invalidate item group queries
                queryClient.invalidateQueries(["getItemGroups"]);
                queryClient.invalidateQueries(["getGroupSubGroups"]);

                refetchItemGroups();
            } catch (error) {
                enqueueSnackbar("Failed to update item group name", {
                    variant: "error",
                });
            } finally {
                setUpdatingName(null);
            }
        }
        setEditingName(null);
        setNewName("");
    };

    const handleNameCancel = () => {
        setEditingName(null);
        setNewName("");
    };

    const checkAndCreateOrUpdateGroup = async (groupData) => {
        try {
            // Check if group already exists in the full itemGroups list (including show_pos = 0)
            const existingGroup = itemGroups.find(
                (group) => group.item_group_name === groupData.item_group_name
            );

            if (existingGroup && existingGroup.showpos !== 1) {
                // Group exists but show_pos is not 1, update it
                await updateItemGroup.mutateAsync({
                    doctype: "Item Group",
                    name: existingGroup.item_group_name,
                    formData: {
                        ...existingGroup,
                        showpos: 1,
                    },
                });
                enqueueSnackbar("Item Group updated to show in POS", {
                    variant: "success",
                });
                // Invalidate item group queries
                queryClient.invalidateQueries(["getItemGroups"]);
                queryClient.invalidateQueries(["getGroupSubGroups"]);
            } else if (!existingGroup) {
                // Group doesn't exist, create new one
                await createItemGroup.mutateAsync({
                    doctype: "Item Group",
                    value: {
                        ...groupData,
                        showpos: 1, // Always set showpos to 1 for new groups
                    },
                });
                enqueueSnackbar("Item Group created successfully", {
                    variant: "success",
                });
                // Invalidate item group queries
                queryClient.invalidateQueries(["getItemGroups"]);
                queryClient.invalidateQueries(["getGroupSubGroups"]);
            } else {
                // Group already exists with showpos = 1
                enqueueSnackbar(
                    "Item Group already exists and is visible in POS",
                    { variant: "info" }
                );
            }
        } catch (error) {
            enqueueSnackbar("Failed to create/update item group", {
                variant: "error",
            });
        }
    };

    const handleDelete = (itemGroup) => {
        setItemGroupToDelete(itemGroup);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (itemGroupToDelete) {
            setIsDeleting(true);
            try {
                await deleteItemGroup.mutateAsync({
                    doctype: "Item Group",
                    name: itemGroupToDelete.item_group_name,
                });
                enqueueSnackbar("Item Group deleted successfully", {
                    variant: "success",
                });
                // Invalidate item group queries
                queryClient.invalidateQueries(["getItemGroups"]);
                queryClient.invalidateQueries(["getGroupSubGroups"]);

                refetchItemGroups();
            } catch (error) {
                enqueueSnackbar("Failed to delete item group", {
                    variant: "error",
                });
            } finally {
                setIsDeleting(false);
            }
        }
        setDeleteDialogOpen(false);
        setItemGroupToDelete(null);
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setItemGroupToDelete(null);
    };

    const handleDragStart = (start) => {
        setIsDragging(true);
    };

    const handleDragEnd = async (result) => {
        // Always reset drag state first
        setIsDragging(false);

        // Use setTimeout to ensure state update is processed
        setTimeout(() => {
            setIsDragging(false);
        }, 0);

        if (!result.destination) {
            return;
        }

        const { draggableId, destination } = result;
        const draggedGroup = itemGroups.find(
            (g) => g.item_group_name === draggableId
        );

        if (!draggedGroup) {
            return;
        }

        // Determine new parent based on drop target
        let newParent = null;
        if (destination.droppableId === "child-groups") {
            // Dropped in child groups area - make it parentless
            newParent = null;
        } else {
            // Dropped on a specific parent group - set that as the new parent
            newParent = destination.droppableId;
        }

        // Don't allow dropping on the same parent
        if (newParent === draggedGroup.parent_item_group) {
            return;
        }

        try {
            await updateItemGroup.mutateAsync({
                doctype: "Item Group",
                name: draggedGroup.item_group_name,
                formData: {
                    parent_item_group: newParent,
                },
            });
            enqueueSnackbar("Item Group moved successfully", {
                variant: "success",
            });
            // Invalidate item group queries
            queryClient.invalidateQueries(["getItemGroups"]);
            queryClient.invalidateQueries(["getGroupSubGroups"]);

            // Refresh the groups after successful move
            refetchItemGroups();
        } catch (error) {
            enqueueSnackbar("Failed to move item group", { variant: "error" });
        } finally {
            // Ensure drag state is reset even if there's an error
            setIsDragging(false);
        }
    };

    const renderParentGroup = (group, level = 0) => {
        const isExpanded = expandedGroups.has(group.item_group_name);
        const hasChildren = group.children && group.children.length > 0;
        const isFilterActive = selectedParentFilters.has(group.item_group_name);
        const isUpdating = updatingName === group.item_group_name;

        return (
            <React.Fragment key={group.item_group_name}>
                <Droppable droppableId={group.item_group_name}>
                    {(provided, snapshot) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                                backgroundColor: snapshot.isDraggingOver
                                    ? "primary.light"
                                    : isFilterActive
                                    ? "primary.light"
                                    : "inherit",
                                border: snapshot.isDraggingOver
                                    ? "2px dashed #1976d2"
                                    : "none",
                                borderRadius: snapshot.isDraggingOver ? 2 : 0,
                                minHeight: 64,
                                transition: "all 0.2s ease-in-out",
                            }}
                        >
                            {snapshot.isDraggingOver ? (
                                // Simplified view when dragging over this specific group
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    height="64px"
                                    px={2}
                                >
                                    <Typography
                                        variant="h6"
                                        color="primary.main"
                                        fontWeight="bold"
                                        textAlign="center"
                                    >
                                        Drop here to update parent_item_group to
                                        "{group.item_group_name}"
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <ListItem
                                        sx={{
                                            pl: level * 3 + 1,
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                            },
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Box position="relative">
                                                <Avatar
                                                    src={
                                                        group.image
                                                            ? `${config.baseUrl}${group.image}`
                                                            : undefined
                                                    }
                                                    alt={group.item_group_name}
                                                    onClick={() =>
                                                        uploadingImage !==
                                                            group.item_group_name &&
                                                        handleAvatarClick(group)
                                                    }
                                                    sx={{
                                                        cursor:
                                                            uploadingImage ===
                                                            group.item_group_name
                                                                ? "default"
                                                                : "pointer",
                                                        "&:hover":
                                                            uploadingImage !==
                                                                group.item_group_name && {
                                                                opacity: 0.8,
                                                                transform:
                                                                    "scale(1.05)",
                                                            },
                                                        transition:
                                                            "all 0.2s ease-in-out",
                                                        opacity:
                                                            uploadingImage ===
                                                            group.item_group_name
                                                                ? 0.7
                                                                : 1,
                                                    }}
                                                >
                                                    {!group.image &&
                                                        (hasChildren ? (
                                                            isExpanded ? (
                                                                <FolderOpen />
                                                            ) : (
                                                                <Folder />
                                                            )
                                                        ) : (
                                                            group.item_group_name
                                                                .charAt(0)
                                                                .toUpperCase()
                                                        ))}
                                                </Avatar>
                                                {uploadingImage ===
                                                    group.item_group_name && (
                                                    <CircularProgress
                                                        size={24}
                                                        sx={{
                                                            position:
                                                                "absolute",
                                                            top: "50%",
                                                            left: "50%",
                                                            marginTop: "-12px",
                                                            marginLeft: "-12px",
                                                            color: "primary.main",
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </ListItemAvatar>

                                        <ListItemText
                                            primary={
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={1}
                                                    flexWrap="wrap"
                                                >
                                                    {editingName ===
                                                    group.item_group_name ? (
                                                        <TextField
                                                            value={newName}
                                                            onChange={(e) =>
                                                                setNewName(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            onBlur={() =>
                                                                handleNameSave(
                                                                    group.item_group_name
                                                                )
                                                            }
                                                            onFocus={(e) =>
                                                                e.target.select()
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                    "Enter"
                                                                ) {
                                                                    handleNameSave(
                                                                        group.item_group_name
                                                                    );
                                                                } else if (
                                                                    e.key ===
                                                                    "Escape"
                                                                ) {
                                                                    handleNameCancel();
                                                                }
                                                            }}
                                                            size="small"
                                                            autoFocus
                                                            variant="outlined"
                                                            sx={{
                                                                minWidth: 200,
                                                            }}
                                                            inputProps={{
                                                                onFocus: (e) =>
                                                                    e.target.select(),
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                        >
                                                            <Typography
                                                                variant="subtitle1"
                                                                onClick={() =>
                                                                    !isUpdating &&
                                                                    handleNameClick(
                                                                        group
                                                                    )
                                                                }
                                                                sx={{
                                                                    cursor: isUpdating
                                                                        ? "default"
                                                                        : "pointer",
                                                                    "&:hover":
                                                                        !isUpdating && {
                                                                            color: "primary.main",
                                                                            textDecoration:
                                                                                "underline",
                                                                        },
                                                                    transition:
                                                                        "all 0.2s ease-in-out",
                                                                    opacity:
                                                                        isUpdating
                                                                            ? 0.7
                                                                            : 1,
                                                                }}
                                                                title={
                                                                    isUpdating
                                                                        ? "Updating..."
                                                                        : "Click to edit name"
                                                                }
                                                            >
                                                                {
                                                                    group.item_group_name
                                                                }
                                                            </Typography>
                                                            {isUpdating && (
                                                                <CircularProgress
                                                                    size={16}
                                                                    sx={{
                                                                        color: "primary.main",
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />

                                        <ListItemSecondaryAction>
                                            <Box display="flex" gap={1}>
                                                <IconButton
                                                    onClick={() =>
                                                        handleFilterToggle(
                                                            group.item_group_name
                                                        )
                                                    }
                                                    color={
                                                        isFilterActive
                                                            ? "primary"
                                                            : "default"
                                                    }
                                                    title={
                                                        isFilterActive
                                                            ? "Clear filter"
                                                            : "Filter child groups"
                                                    }
                                                >
                                                    {isFilterActive ? (
                                                        <Visibility />
                                                    ) : (
                                                        <VisibilityOff />
                                                    )}
                                                </IconButton>
                                                {hasChildren && (
                                                    <IconButton
                                                        onClick={() =>
                                                            handleToggleExpand(
                                                                group.item_group_name
                                                            )
                                                        }
                                                    >
                                                        {isExpanded ? (
                                                            <ExpandLess />
                                                        ) : (
                                                            <ExpandMore />
                                                        )}
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    onClick={() =>
                                                        onEdit(group)
                                                    }
                                                    color="primary"
                                                >
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() =>
                                                        handleDelete(group)
                                                    }
                                                    color="error"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Box>
                                        </ListItemSecondaryAction>
                                    </ListItem>

                                    {hasChildren && (
                                        <Collapse
                                            in={isExpanded}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <List
                                                component="div"
                                                disablePadding
                                            >
                                                {group.children.map((child) =>
                                                    renderParentGroup(
                                                        child,
                                                        level + 1
                                                    )
                                                )}
                                            </List>
                                        </Collapse>
                                    )}
                                </>
                            )}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </React.Fragment>
        );
    };

    const renderChildGroup = (group, index) => {
        const isUpdating = updatingName === group.item_group_name;

        return (
            <Draggable
                key={group.item_group_name}
                draggableId={group.item_group_name}
                index={index}
            >
                {(provided, snapshot) => (
                    <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                            backgroundColor: snapshot.isDragging
                                ? "action.hover"
                                : "inherit",
                            "&:hover": {
                                backgroundColor: "action.hover",
                            },
                        }}
                    >
                        <div {...provided.dragHandleProps}>
                            <DragIndicator
                                sx={{ mr: 1, color: "text.secondary" }}
                            />
                        </div>

                        <ListItemAvatar>
                            <Box position="relative">
                                <Avatar
                                    src={
                                        group.image
                                            ? `${config.baseUrl}${group.image}`
                                            : undefined
                                    }
                                    alt={group.item_group_name}
                                    onClick={() =>
                                        uploadingImage !==
                                            group.item_group_name &&
                                        handleAvatarClick(group)
                                    }
                                    sx={{
                                        cursor:
                                            uploadingImage ===
                                            group.item_group_name
                                                ? "default"
                                                : "pointer",
                                        "&:hover": uploadingImage !==
                                            group.item_group_name && {
                                            opacity: 0.8,
                                            transform: "scale(1.05)",
                                        },
                                        transition: "all 0.2s ease-in-out",
                                        opacity:
                                            uploadingImage ===
                                            group.item_group_name
                                                ? 0.7
                                                : 1,
                                    }}
                                >
                                    {!group.image &&
                                        group.item_group_name
                                            .charAt(0)
                                            .toUpperCase()}
                                </Avatar>
                                {uploadingImage === group.item_group_name && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                            color: "primary.main",
                                        }}
                                    />
                                )}
                            </Box>
                        </ListItemAvatar>

                        <ListItemText
                            primary={
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    flexWrap="wrap"
                                >
                                    {editingName === group.item_group_name ? (
                                        <TextField
                                            value={newName}
                                            onChange={(e) =>
                                                setNewName(e.target.value)
                                            }
                                            onBlur={() =>
                                                handleNameSave(
                                                    group.item_group_name
                                                )
                                            }
                                            onFocus={(e) => e.target.select()}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleNameSave(
                                                        group.item_group_name
                                                    );
                                                } else if (e.key === "Escape") {
                                                    handleNameCancel();
                                                }
                                            }}
                                            size="small"
                                            autoFocus
                                            variant="outlined"
                                            sx={{ minWidth: 200 }}
                                            inputProps={{
                                                onFocus: (e) =>
                                                    e.target.select(),
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                onClick={() =>
                                                    !isUpdating &&
                                                    handleNameClick(group)
                                                }
                                                sx={{
                                                    cursor: isUpdating
                                                        ? "default"
                                                        : "pointer",
                                                    "&:hover": !isUpdating && {
                                                        color: "primary.main",
                                                        textDecoration:
                                                            "underline",
                                                    },
                                                    transition:
                                                        "all 0.2s ease-in-out",
                                                    opacity: isUpdating
                                                        ? 0.7
                                                        : 1,
                                                }}
                                                title={
                                                    isUpdating
                                                        ? "Updating..."
                                                        : "Click to edit name"
                                                }
                                            >
                                                {group.item_group_name}
                                            </Typography>
                                            {isUpdating && (
                                                <CircularProgress
                                                    size={16}
                                                    sx={{
                                                        color: "primary.main",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                    {group.parent_item_group && (
                                        <Chip
                                            label={`Parent: ${group.parent_item_group}`}
                                            size="small"
                                            color="info"
                                            variant="filled"
                                        />
                                    )}
                                </Box>
                            }
                            secondary={
                                group.parent_item_group
                                    ? "Child Group"
                                    : "Root Level Group"
                            }
                        />

                        <ListItemSecondaryAction>
                            <Box display="flex" gap={1}>
                                <IconButton
                                    onClick={() => onEdit(group)}
                                    color="primary"
                                >
                                    <Edit />
                                </IconButton>
                                <IconButton
                                    onClick={() => handleDelete(group)}
                                    color="error"
                                >
                                    <Delete />
                                </IconButton>
                            </Box>
                        </ListItemSecondaryAction>
                    </ListItem>
                )}
            </Draggable>
        );
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Item Groups Management (Show in POS Only)
            </Typography>

            {(selectedParentFilters.size > 0 || showAllItemGroupsChildren) && (
                <Box
                    mb={2}
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                    alignItems="center"
                >
                    {showAllItemGroupsChildren && (
                        <Chip
                            label="Showing: All Item Groups Children"
                            onDelete={handleToggleAllItemGroupsFilter}
                            color="secondary"
                            variant="filled"
                        />
                    )}
                    {Array.from(selectedParentFilters).map((filter) => (
                        <Chip
                            key={filter}
                            label={`Filtering: ${filter}`}
                            onDelete={() => handleFilterToggle(filter)}
                            color="primary"
                            variant="outlined"
                        />
                    ))}
                    <Button
                        startIcon={<FilterListOff />}
                        onClick={handleResetAllFilters}
                        size="small"
                        variant="outlined"
                    >
                        Reset All Filters
                    </Button>
                </Box>
            )}

            <DragDropContext
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <Grid container spacing={2}>
                    {/* Left Column - Parent Groups (is_group = 1) */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1}>
                            <Box p={2}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={2}
                                >
                                    <Typography variant="subtitle1">
                                        Parent Groups
                                    </Typography>
                                    <Button
                                        startIcon={<Add />}
                                        onClick={handleAddParentGroup}
                                        variant="contained"
                                        size="small"
                                        color="primary"
                                    >
                                        Add Parent Group
                                    </Button>
                                </Box>
                                <List
                                    sx={{
                                        minHeight: 200,
                                    }}
                                >
                                    {parentGroups.map((group) =>
                                        renderParentGroup(group)
                                    )}
                                    {parentGroups.length === 0 && (
                                        <ListItem>
                                            <ListItemText
                                                primary="No parent groups found"
                                                secondary="Click 'Add Parent Group' to create one"
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Right Column - Child Groups (is_group = 0) */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1}>
                            <Box p={2}>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={2}
                                    flexWrap="wrap"
                                    gap={1}
                                >
                                    <Typography variant="subtitle1">
                                        Child Groups (is_group = 0)
                                        {showAllItemGroupsChildren &&
                                            ` - All Item Groups Children`}
                                        {selectedParentFilters.size > 0 &&
                                            ` - Filtered by ${
                                                selectedParentFilters.size
                                            } group${
                                                selectedParentFilters.size > 1
                                                    ? "s"
                                                    : ""
                                            }`}
                                    </Typography>
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        <Button
                                            startIcon={<Folder />}
                                            onClick={
                                                handleToggleAllItemGroupsFilter
                                            }
                                            variant={
                                                showAllItemGroupsChildren
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            size="small"
                                            color="secondary"
                                        >
                                            {showAllItemGroupsChildren
                                                ? "Hide"
                                                : "Show"}{" "}
                                            All Item Groups Children
                                        </Button>
                                        <Button
                                            startIcon={<Add />}
                                            onClick={handleAddChildGroup}
                                            variant="contained"
                                            size="small"
                                            color="secondary"
                                        >
                                            Add Child Group
                                        </Button>
                                    </Box>
                                </Box>
                                <Droppable droppableId="child-groups">
                                    {(provided, snapshot) => (
                                        <Box
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            sx={{
                                                minHeight: 200,
                                                backgroundColor:
                                                    snapshot.isDraggingOver
                                                        ? "warning.light"
                                                        : "inherit",
                                                border: snapshot.isDraggingOver
                                                    ? "2px dashed #ff9800"
                                                    : "none",
                                                borderRadius: 1,
                                                position: "relative",
                                            }}
                                        >
                                            {/* Always render the List structure for DnD consistency */}
                                            <List
                                                sx={{
                                                    width: "100%",
                                                    minHeight: 200,
                                                    opacity: isDragging
                                                        ? 0.2
                                                        : 1,
                                                    transition:
                                                        "opacity 0.2s ease-in-out",
                                                }}
                                            >
                                                {filteredChildGroups.map(
                                                    (group, index) =>
                                                        renderChildGroup(
                                                            group,
                                                            index
                                                        )
                                                )}
                                                {provided.placeholder}
                                                {filteredChildGroups.length ===
                                                    0 && (
                                                    <ListItem>
                                                        <ListItemText
                                                            primary={
                                                                selectedParentFilters.size >
                                                                0
                                                                    ? `No child groups for selected filters`
                                                                    : "No child groups found"
                                                            }
                                                            secondary="Click 'Add Child Group' to create one or drag items here"
                                                        />
                                                    </ListItem>
                                                )}
                                            </List>

                                            {/* Overlay message when dragging */}
                                            {isDragging && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        backgroundColor:
                                                            snapshot.isDraggingOver
                                                                ? "rgba(255, 152, 0, 0.1)"
                                                                : "rgba(0, 0, 0, 0.05)",
                                                        pointerEvents: "none",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        color="warning.main"
                                                        fontWeight="bold"
                                                        textAlign="center"
                                                        sx={{ px: 2 }}
                                                    >
                                                        Drop here to reset the
                                                        parent group
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Droppable>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </DragDropContext>

            <ConfirmationDialog
                open={deleteDialogOpen}
                onConfirm={handleConfirmDelete}
                onClose={handleCancelDelete}
                title="Delete Item Group"
                message={`Are you sure you want to delete "${itemGroupToDelete?.item_group_name}"? This action cannot be undone and may affect related items.`}
                confirmText="Delete"
                confirmColor="error"
                cancelText="Cancel"
                loading={isDeleting}
            />
        </Box>
    );
};

export default ItemGroupsTab;
